import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { throwError } from 'rxjs';
import { TuiDialogContext } from '@taiga-ui/core';
import { TuiPreviewDialogService } from '@taiga-ui/kit';
import { EnvService } from '@src/app/modules/env';
import { BaseComponent } from '@src/app/modules/base';

import { FileType, ImageType } from './types';
import {
  MAX_HEIGHT_IMAGE_DESKTOP,
  MAX_HEIGHT_IMAGE_MOBILE,
  MAX_WIDTH_IMAGE_DESKTOP,
  MAX_WIDTH_IMAGE_MOBILE,
} from './constants';

@Component({
  selector: 'app-images-group-view',
  templateUrl: './images-group-view.component.html',
  styleUrls: ['./images-group-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ImagesGroupViewComponent extends BaseComponent implements OnChanges, OnInit {
  private readonly env = inject(EnvService);

  private readonly previewDialogService = inject(TuiPreviewDialogService);
  @ViewChild('previewTmp') readonly previewTmp?: TemplateRef<TuiDialogContext<void>>;
  protected previewImageIndex = 0;

  @Input({ required: true }) filesCount = 0;
  @Input({ required: true }) files: FileType[] = [];

  @Input() width = this.env.isMobilePlatform ? MAX_WIDTH_IMAGE_MOBILE : MAX_WIDTH_IMAGE_DESKTOP;
  @Input() height = this.env.isMobilePlatform ? MAX_HEIGHT_IMAGE_MOBILE : MAX_HEIGHT_IMAGE_DESKTOP;

  images: ImageType[] = [];

  get isGroup() {
    return this.filesCount > 1;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['files']) {
      if (this.files) {
        this.mapFilesToImages(this.files);

        this.cdr.markForCheck();
      }
    }
  }

  ngOnInit(): void {
    this.images = Array.from({ length: this.filesCount }, () => {
      return { loading: true } as ImageType;
    });
  }

  trackByFn(index: number): number {
    return index;
  }

  onClickPhoto(index: number) {
    if (!this.previewTmp) {
      return;
    }

    this.previewImageIndex = index;

    this.previewDialogService.open(this.previewTmp).subscribe();
  }

  private mapFilesToImages(files: FileType[]) {
    files.forEach((file, index) => {
      const { blob, fileName } = file;
      const imageSrc = URL.createObjectURL(blob);
      try {
        const imageEl = new Image();
        imageEl.src = imageSrc;

        imageEl.onload = () => {
          const [width, height] = this.calculateImageSize(imageEl);

          this.images[index] = {
            src: imageSrc,
            fileName,
            width,
            height,
            loading: false,
          };

          this.cdr.markForCheck();
        };
      } catch (error) {
        throwError(() => error);
      }
    });
  }

  private calculateImageSize(imageEl: HTMLImageElement): number[] {
    let width = 0;
    let height = 0;

    const naturalWidth = imageEl.naturalWidth;
    const naturalHeight = imageEl.naturalHeight;

    if (naturalHeight > naturalWidth) {
      if (naturalHeight > this.height) {
        height = this.height;
        width = (naturalWidth * this.height) / naturalHeight;
      } else {
        height = naturalHeight;
        width = naturalWidth;
      }
    } else if (naturalWidth > this.width) {
      width = this.width;
      height = (naturalHeight * this.width) / naturalWidth;
    } else {
      width = naturalWidth;
      height = naturalHeight;
    }

    return [width, height];
  }
}
